/**
 * Daily check-in form. Five 1-5 scales + free notes.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { Icon } from '@k-os/ui';
import {
  useTodayCheckin,
  useUpsertCheckin,
  useHealthCheckins,
} from '../../api/health';
import styles from './health.module.css';

const SCALE_LABELS: Record<string, [string, string, string, string, string]> = {
  energy: ['Drained', 'Low', 'OK', 'Good', 'High'],
  mood: ['Low', 'Off', 'OK', 'Good', 'Great'],
  soreness: ['None', 'Mild', 'Moderate', 'Sore', 'Very sore'],
  stress: ['Calm', 'Light', 'OK', 'Tense', 'Overloaded'],
  sleepQuality: ['Poor', 'Light', 'OK', 'Good', 'Great'],
};

type Field = keyof typeof SCALE_LABELS;
const FIELDS: Field[] = ['energy', 'mood', 'sleepQuality', 'stress', 'soreness'];

export function HealthCheckinScreen() {
  const today = useTodayCheckin();
  const upsert = useUpsertCheckin();
  const history = useHealthCheckins();
  const todayDate = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState<Record<Field, number | null>>({
    energy: null,
    mood: null,
    soreness: null,
    stress: null,
    sleepQuality: null,
  });
  const [notes, setNotes] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (today.data) {
      setForm({
        energy: today.data.energy,
        mood: today.data.mood,
        soreness: today.data.soreness,
        stress: today.data.stress,
        sleepQuality: today.data.sleepQuality,
      });
      setNotes(today.data.notes);
    }
  }, [today.data]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    upsert.mutate(
      { date: todayDate, patch: { ...form, notes } },
      { onSuccess: () => setSaved(true) },
    );
  }

  function setField(field: Field, value: number) {
    setForm((f) => ({ ...f, [field]: f[field] === value ? null : value }));
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Daily check-in</h2>
          <div className={styles.subtitle}>
            How are you doing today? One row per 1-5 scale; notes are free-form.
          </div>
        </div>
      </header>

      <form className={styles.card} onSubmit={onSubmit}>
        <div className={styles.formGrid}>
          {FIELDS.map((f) => (
            <FieldRow key={f} field={f} value={form[f]} onPick={setField} />
          ))}
          <label className={styles.label} htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            className={styles.textarea}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What's going on? What does the agent need to know?"
          />
        </div>

        {upsert.isError && (
          <div className={styles.error}>Save failed. Try again.</div>
        )}

        <div className={styles.formFoot}>
          {saved && !upsert.isPending && (
            <span className={styles.muted}>Saved</span>
          )}
          <button
            type="submit"
            className="kos-btn kos-btn-primary"
            disabled={upsert.isPending}
          >
            <Icon name="check" size={12} />
            {upsert.isPending ? ' Saving…' : ' Save check-in'}
          </button>
        </div>
      </form>

      <section style={{ marginTop: 24 }}>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>Recent check-ins</h3>
          <Link to="/health" className={styles.cardSub}>
            today ›
          </Link>
        </div>
        {history.isLoading ? (
          <div className={styles.muted}>Loading…</div>
        ) : (history.data?.checkins ?? []).length === 0 ? (
          <div className={styles.empty}>No check-ins logged yet.</div>
        ) : (
          <div className={styles.list}>
            {(history.data?.checkins ?? []).slice(0, 10).map((c) => (
              <div key={c.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <div className={styles.rowTitle}>{c.date}</div>
                  <div className={styles.rowMeta}>
                    E {c.energy ?? '—'} · M {c.mood ?? '—'} · S {c.sleepQuality ?? '—'} ·
                    St {c.stress ?? '—'} · So {c.soreness ?? '—'}
                  </div>
                </div>
                {c.notes && (
                  <div className={styles.rowMeta} style={{ maxWidth: 380, textAlign: 'right' }}>
                    {c.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function FieldRow({
  field,
  value,
  onPick,
}: {
  field: Field;
  value: number | null;
  onPick: (field: Field, value: number) => void;
}) {
  const labels = SCALE_LABELS[field]!;
  return (
    <>
      <label className={styles.label}>{capitalise(field)}</label>
      <div>
        <div className={styles.scale} role="radiogroup" aria-label={field}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              className={`${styles.scaleButton} ${value === n ? styles.scaleSelected : ''}`}
              onClick={() => onPick(field, n)}
              aria-pressed={value === n}
              aria-label={`${field} ${n} — ${labels[n - 1]}`}
            >
              {n}
            </button>
          ))}
        </div>
        {value !== null && (
          <div className={styles.helpText}>{labels[value - 1]}</div>
        )}
      </div>
    </>
  );
}

function capitalise(s: string): string {
  return s.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
}
